import { Injectable } from '@angular/core';
import { SharedStorageService } from 'ngx-store';
import { EntityTypes, Step } from '../../../../shared/models/cerebrum-core';
import { NavigationEnd, Router } from '@angular/router';
import find from 'lodash/find';
import {Observable, Subject} from 'rxjs';
import { datadogRum } from '@datadog/browser-rum';
import {FlowType} from "../../../../shared/models/state/Flow";

const ENTITY_TYPE_STORAGE = 'entityType';
const ENTITY_ID_STORAGE = 'entityId';
const ENVIRONMENT_STORAGE = 'environment';
const USER_INFO_STORAGE = 'userInfo';
const IS_LOGGED_IN_STORAGE = 'loggedIn';
const IS_FLOW_CHANGED = 'isFlowChanged';
const IS_NEW_FLOW_SAVED = 'isNewFlowSaved';
const REQUEST_FLOW_LOAD = 'requestFlowLoad';
const REQUEST_ROUTE_LOAD = 'requestRouteLoad';
const USER_ACCESS_TOKEN = 'user_access_token';
const ACCESS_TOKEN = 'access_token';
const MANAGEMENT_NOTIFICATION = 'managementNotification';
const ENTITY_LIST = 'entityList';

const FLOW_PATH_NAME = 'flowPathName';
const FLOW_NAME = 'flowName';
const FLOW_REF = 'flowRef';

// TODO IA 28 Nov 2019 -> need to move into a model package
export class FlowMetaData {
  flowId: string;
  flowName: string;
  directory: string;
  flowStatus: string;
  flowType: FlowType;

  constructor(flowId: string, flowName: string, directory: string, flowStatus: string = null, flowType: FlowType = null) {
    this.flowId = flowId;
    this.flowName = flowName;
    this.directory = directory;
    this.flowStatus = flowStatus;
    this.flowType = flowType;
  }
}

// TODO IA 28 Nov 2019 -> need to move into a model package
export class Flow {
  metaData: FlowMetaData;
  steps: Step[];

  constructor(metaData: FlowMetaData, steps: Step[]) {
    this.metaData = metaData;
    this.steps = steps;
  }
}

const NEW_FLOW_PATH = '__NEW_FLOW__';

export interface Environment {
  entityType: string;
  entityId: string;
  environment: string;
}

@Injectable()
export class SharedService {
  constructor(private sharedStorageService: SharedStorageService, private router: Router) {
    this.router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) {
        this.isFlowOpen = e.url.includes('/flow/');
      }
    });
  }

  set setUserAccessToken(accessToken: String) {
    this.set(USER_ACCESS_TOKEN, accessToken.replace(/"/g, ''));
  }

  get getUserAccessToken() {
    return this.get(USER_ACCESS_TOKEN);
  }

  set setAccessToken(accessToken: string) {
    this.set(ACCESS_TOKEN, accessToken.replace(/"/g, ''));
  }

  get getAccessToken() {
    return this.get(ACCESS_TOKEN);
  }

  set setFlowName(flowName: string) {
    this.set(FLOW_NAME, flowName);
  }

  get getFlowName() {
    return this.get(FLOW_NAME);
  }

  get getFlowNameToPath() {
    return this.getFlowPathName() ? this.getFlowPathName().replace(',', '/') : '';
  }

  set setFlowRef(flowRef: string) {
    this.set(FLOW_REF, flowRef);
  }

  get getFlowRef() {
    return this.get(FLOW_REF);
  }

  set setEntityList(entityList: any) {
    this.set(ENTITY_LIST, entityList);
  }

  get getEntityList(): any {
    return this.get(ENTITY_LIST);
  }

  flows = [];
  flow: Flow;
  isFlowPristine = true;
  isFlowOpen: Boolean = false;
  envChanged: Object = {
    key: null,
    value: null,
    callback: null,
  };
  defClients: Object = {
    client: this.getEntityId(),
    funding_source: null,
    vendor: null,
    system: '0',
    template: '0'
  };

  globalEnvironment: Environment = {
    entityType: '',
    entityId: '',
    environment: 'dev',
  };

  requestEnvironment: Environment = this.globalEnvironment;
  private isNodeSavedSource = new Subject<any>();

  isNodeSaved$ = this.isNodeSavedSource.asObservable();

    private flowsUpdated: Boolean = false;
    private entityConfigs: any;
    private chat2payExtensions: Observable<any>;

  getFlowsUpdated() {
    return this.flowsUpdated;
  }

  setFlowsUpdated(status: Boolean = false) {
    this.flowsUpdated = status;
  }

  getDefClients() {
    return this.defClients;
  }

  private get(id: string) {
    return this.sharedStorageService.get(id);
  }

  private set(id: string, value: any) {
    this.sharedStorageService.set(id, value);
  }

  isNodeSaved(value) {
    this.isNodeSavedSource.next(value);
  }

  clearStorage() {
    this.sharedStorageService.clear();
  }

  changeRequestEnvironment(value: any) {
    this.changeRequest('environment', value, this.setEnvironment.bind(this));
  }

  changeRequestEntityId(value: any) {
    if (this.getEntityType()) {
      this.defClients[this.getEntityType().toLocaleLowerCase()] = value;
    }
    this.changeRequest('entityId', value, this.setEntityId.bind(this));
  }

  changeRequestEntityType(value: any) {
    this.changeRequest('entityType', value, this.setEntityType.bind(this));
  }

  private changeRequest(key: string, value: any, callback) {
    this.clearEntityConfigs();
    this.clearChat2PayExtensions();
    this.setFlowsUpdated(true);
    this.envChanged = { key, value, callback };
    if (!this.isFlowOpen) {
      this.setRequestData();
    }
    this.router.navigate(['/']);
  }

  setRequestData() {
    // @ts-ignore
    const { key, value, callback } = this.envChanged;
    this.clearEnvChanged();
    this.requestEnvironment = { ...this.globalEnvironment };
    this.requestEnvironment[key] = value;
    callback(value);
    this.setFlowPathName('');
  }

  clearEnvChanged() {
    this.envChanged = {
      key: null,
      value: null,
      callback: null,
    };
  }

  addFlows(flow) {
    if (!this.flows) {
      this.flows = [];
    }
    const meta_data = new FlowMetaData(flow.id, flow.name, flow.path);
    flow = new Flow(meta_data, []);
    this.flows.push(flow);
  }

  clearFlows() {
    this.flows = [];
  }

  updateFlows(flows) {
    this.flows = [];
    if (flows) {
      for (const flow of flows) {
        if (flow.children) {
          for (const child of flow.children) {
            this.addFlows(child);
          }
        } else {
          this.addFlows(flow);
        }
      }
    }
  }

  getFlow(flowId: string): Flow {
    if (this.flows) {
      this.flows.forEach((flow) => {
        if (flow.metaData.flowId === flowId) {
          this.flow = flow;
        }
      });
    }
    return this.flow;
  }

  setFlow(flow: Flow) {
    this.flow = flow;
  }

  getFlowNameFromMetaData() {
    if (!this.flow) {
      return '';
    }
    return this.flow.metaData ? this.flow.metaData.flowName : '';
  }

  getGlobalEnvironment() {
    return this.globalEnvironment;
  }

  setGlobalEnvironment(data: Environment) {
    this.setEntityId(data.entityId);
    this.setEntityType(data.entityType);
    this.setEnvironment(data.environment);
    this.clearEntityConfigs();
    this.clearChat2PayExtensions();
  }

  getEntityType(): EntityTypes {
    return this.get(ENTITY_TYPE_STORAGE);
  }

  setEntityType(entityType: string) {
    if (entityType) {
      this.globalEnvironment.entityType = entityType.toUpperCase();
      this.set(ENTITY_TYPE_STORAGE, entityType.toUpperCase());
      if (this.defClients[entityType]) {
        this.requestEnvironment.entityId = this.defClients[entityType];
        this.setEntityId(this.defClients[entityType]);
      }
    }
  }

  setEntityId(entityId: string) {
    this.globalEnvironment.entityId = entityId;
    this.set(ENTITY_ID_STORAGE, entityId);
  }

  getEntityId(): string {
    return this.get(ENTITY_ID_STORAGE);
  }

    setUserInfo(userInfo: any) {
        this.set(USER_INFO_STORAGE, userInfo);
        this.addCustomAttributes();
    }

  getUserInfo(): any {
    return this.get(USER_INFO_STORAGE);
  }

  setLoggedIn(isLoggedIn: boolean) {
    this.set(IS_LOGGED_IN_STORAGE, isLoggedIn);
  }

  getLoggedIn(): boolean {
    return this.get(IS_LOGGED_IN_STORAGE);
  }

  setFlowPathName(flowName: string) {
    this.set(FLOW_PATH_NAME, flowName);
    this.addCustomAttributes();
  }

  getFlowPathName(): string {
    return this.get(FLOW_PATH_NAME);
  }

  setEnvironment(environment: string) {
    this.globalEnvironment.environment = environment;
    this.set(ENVIRONMENT_STORAGE, environment);
  }

  getEnvironment(): string {
    return this.get(ENVIRONMENT_STORAGE);
  }

  getNewFlowName(): string {
    return NEW_FLOW_PATH;
  }

  setIsFlowChanged(changed: boolean) {
    this.set(IS_FLOW_CHANGED, changed);
  }

  getIsFlowChanged(): boolean {
    return this.get(IS_FLOW_CHANGED);
  }

  setRequestFlowLoad(changed: string) {
    this.set(REQUEST_FLOW_LOAD, changed);
  }

  getRequestFlowLoad(): string {
    return this.get(REQUEST_FLOW_LOAD);
  }

  setRequestRouteLoad(changed: string) {
    this.set(REQUEST_ROUTE_LOAD, changed);
  }

  getRequestRouteLoad(): string {
    return this.get(REQUEST_ROUTE_LOAD);
  }

  setNewFlowSaved(saved: boolean) {
    this.set(IS_NEW_FLOW_SAVED, saved);
  }

  isNewFlowSaved(): boolean {
    return this.get(IS_NEW_FLOW_SAVED);
  }

  get isProdEnv(): boolean {
    const env = this.getEnvironment();
    return (env === 'production' || env === 'preprod' || env === 'prod');
  }

  checkSameFlowName(type, params): Boolean {
    let isSameName: Boolean = false;
    switch (type) {
      case 'rename':
        const currFlow: Flow = find(this.flows, (item) => {
          return item.metaData.flowId === params.flowId;
        });
        if (currFlow) {
          const currPath: String = currFlow.metaData ? currFlow.metaData.directory : null;
          if (currPath) {
            const currPathArr: Array<String> = currPath.split('/');
            if (currPathArr) {
              currPathArr.pop();
              const newPath = `${currPathArr.join()}/${params.flowName}`;
              const isRoot = newPath.startsWith('/');
              const sameName: Flow = find(this.flows, (item) => {
                const curDir = isRoot ? '/' + item.metaData.directory : item.metaData.directory;
                return curDir === newPath;
              });
              if (sameName) {
                isSameName = true;
              }
            }
          }
        }
        break;
      case 'create':
      case 'move':
        const samePath: Flow = find(this.flows, (item) => {
          return item.metaData.directory === params.flowPath;
        });
        if (samePath) {
          isSameName = true;
        }
        break;
      default:
        break;
    }

    return isSameName;
  }

  addCustomAttributes() {
    datadogRum.setRumGlobalContext({
      id: this.getUserInfo()['id'],
      entityId: this.getUserInfo()['clientId'],
      entityType: this.getUserInfo()['entityType'],
      flowName: this.getFlowNameToPath,
    });
  }

  set setHasNotifications(notifications: boolean) {
    this.set(MANAGEMENT_NOTIFICATION, notifications);
  }

  get getHasNotifications() {
    return this.get(MANAGEMENT_NOTIFICATION);
  }

  setMetaData(metaData) {
    this.set('metaData', metaData);
  }

  getMetaData(): any {
    return this.get('metaData');
  }

  findFlow(flowId: string) {
    return find(this.getMetaData(), (flow) => {
      return flow.flowId === flowId;
    });
  }

  setFlowStatus(flowId, flowStatus) {
    if (this.flow && this.flow.metaData.flowId === flowId) {
      this.flow.metaData.flowStatus = flowStatus;
    }
  }

  get getFlowStatus(): string {
    if (this.flow && this.flow.metaData) {
      const flow = this.findFlow(this.flow.metaData.flowId);
      if (flow && flow.hasOwnProperty('flowStatus')) {
        return this.findFlow(this.flow.metaData.flowId).flowStatus;
      }
    }
    return;
  }

  get isDeletePending() {
    if (this.getFlowStatus) {
      return ['DELETE_PENDING'].includes(this.getFlowStatus);
    }
    return false;
  }

    canDeleteFlow(status) {
        return !find(['DELETE_PENDING', 'PENDING', 'APPROVED'], o => (o === status));
    }

    getEntityConfigs() {
        return this.entityConfigs;
    }

    setEntityConfigs(configs) {
        this.entityConfigs = configs;
    }

    clearEntityConfigs() {
        this.entityConfigs = null;
    }

    getChat2PayExtensions() {
        return this.chat2payExtensions;
    }

    setChat2PayExtension(chat2payExtensions: any) {
      this.chat2payExtensions = chat2payExtensions;
    }

    clearChat2PayExtensions() {
      this.chat2payExtensions = null;
    }
}
